import { useMutation } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Button, Loader } from '@susu/ui/components'
import { useI18N } from '@susu/i18n/client'

import { ScreenLayout } from '../../components/ScreenLayout'
import Avo from '../../../Avo/Avo'
import { flowProperties } from '../../lib/analytics'
import { useBookingStore } from '../../store'
import { cancelBooking } from '../Timeslots/api/booking-api'

interface CancelProps {
  onClose?: () => void
}

/** `onClose` is accepted for host API parity but unused — no header close on this screen. */
export const Cancel = (_: CancelProps) => {
  const { t } = useI18N()
  const { navigate, bookingId, setState } = useBookingStore()

  const { mutate, isPending, isSuccess, isError, reset } = useMutation({
    mutationFn: cancelBooking,
    onSuccess: () => {
      Avo.bookingToolCancelConfirmed({
        ...flowProperties(),
        bookingId: bookingId ?? '',
      })
      // Read by `BookingToolRoot`'s unmount handler so a completed cancel
      // never also reports `bookingToolPanelClosed`.
      setState({ cancelConfirmed: true })
    },
  })

  // Auto-cancel on mount — same behaviour as the old booking tool.
  // Intentionally run once; mutate/bookingId refs are stable after first render.
  useEffect(() => {
    if (bookingId) mutate()
  }, [])

  const handleReschedule = () => {
    navigate('timeslots')
  }

  return (
    // No header title / close — success copy + Reschedule is the exit path.
    <ScreenLayout>
      {isPending && (
        <div className="flex-1 flex items-center justify-center">
          <Loader ariaLabel={t('common.loading')} className="h-auto w-auto" />
        </div>
      )}

      {isError && !isPending && (
        <div className="flex-1 flex flex-col items-center justify-center gap-24 px-32 text-center">
          <p
            className="text-base leading-[24px] text-text"
            data-testid="cancel-error"
          >
            {t('common.error')}
          </p>
          <Button
            variant="outlined"
            size="m"
            onClick={() => {
              reset()
              mutate()
            }}
            data-testid="cancel-retry"
          >
            {t('common.retry')}
          </Button>
        </div>
      )}

      {isSuccess && (
        <div className="flex-1 flex flex-col items-center justify-center gap-24 px-32 text-center">
          <div className="flex flex-col gap-12">
            <h2
              className="text-[20px] leading-[30px] font-medium text-text tracking-[-0.3px]"
              data-testid="cancel-success-heading"
            >
              {t('cancel.successTitle')}
            </h2>
            <p
              className="text-base leading-[24px] font-light text-text"
              data-testid="cancel-success-body"
            >
              {t('cancel.successBody')}
            </p>
          </div>
          <Button
            size="m"
            onClick={handleReschedule}
            data-testid="cancel-reschedule"
          >
            {t('cancel.rescheduleButton')}
          </Button>
        </div>
      )}
    </ScreenLayout>
  )
}
